/**
 * 个人信息
 *
 * 权限按模块分组展示，方便财务/法务同事核对「我到底能做什么」，
 * 也方便向租户管理员提出权限调整诉求。
 */

import { useState } from 'react';
import { Card, Col, Descriptions, Row, Space, Table, Tag, Typography, Button, Form, Input, Alert, Modal } from 'antd';
import { DATA_SCOPE_LABELS, PERMISSION_BY_CODE, PERMISSION_CATALOG, RISK_LEVEL_LABELS, PrincipalType } from '@ilc/shared';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/PageHeader';
import { feedback } from '@/utils/feedback';
import { authApi } from '@/api/endpoints';
import { performAction } from '@/utils/performAction';

export default function Profile(): React.ReactElement {
  const auth = useAuthStore((s) => s.auth);
  const logout = useAuthStore((s) => s.logout);
  const [pwdForm] = Form.useForm<{ currentPassword: string; newPassword: string; confirm: string }>();
  const [changing, setChanging] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  const isTenantUser = auth?.principalType === PrincipalType.TENANT;

  /**
   * 修改自己的密码。
   *
   * 必须校验当前密码——仅有登录态不足以证明操作者是本人，
   * 否则令牌被盗后攻击者可以直接改密码把账号主人锁在外面。
   */
  const changePassword = async (): Promise<void> => {
    const v = await pwdForm.validateFields();
    setChanging(true);
    const result = await performAction({
      action: () =>
        authApi.changePassword({ currentPassword: v.currentPassword, newPassword: v.newPassword }),
      successMessage: '密码已修改，下次登录请使用新密码',
      offline: () => undefined,
      offlineMessage: '后端改密码接口尚未就绪，本次仅在本地演示',
    });
    setChanging(false);
    if (result.ok) pwdForm.resetFields();
  };

  const myPermissions = PERMISSION_CATALOG.filter((p) => auth?.permissions.includes(p.code));
  const byModule = myPermissions.reduce<Record<string, typeof myPermissions>>((acc, p) => {
    acc[p.module] = acc[p.module] ?? [];
    acc[p.module]!.push(p);
    return acc;
  }, {});

  const missingModules = PERMISSION_CATALOG.filter((p) => !auth?.permissions.includes(p.code));

  return (
    <div className="ilc-page">
      <PageHeader
        title="个人信息"
        description="登录身份、角色、权限范围与账号安全"
        extra={
          <Button danger onClick={() => void logout()}>
            退出登录
          </Button>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="账号信息" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="姓名">{auth?.name}</Descriptions.Item>
              <Descriptions.Item label="账号">{auth?.username}</Descriptions.Item>
              <Descriptions.Item label="主体类型">
                {auth?.principalType === 'PLATFORM' ? (
                  <Tag color="geekblue">平台运营人员（{auth.platformRole}）</Tag>
                ) : (
                  <Tag color="blue">租户用户</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="所属租户">{auth?.tenantName ?? '平台'}</Descriptions.Item>
              <Descriptions.Item label="部门">{auth?.deptId ?? '未分配'}</Descriptions.Item>
              <Descriptions.Item label="角色">
                <Space wrap size={4}>
                  {(auth?.roles ?? []).map((r) => (
                    <Tag key={r.id} color="blue">
                      {r.name}（{r.code}）
                    </Tag>
                  ))}
                  {(auth?.roles ?? []).length === 0 ? <Tag>未分配</Tag> : null}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="权限范围" size="small" style={{ marginTop: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="数据权限">
                {auth ? DATA_SCOPE_LABELS[auth.dataScope] : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="自定义部门范围">
                {auth?.customDeptIds?.length ? auth.customDeptIds.join('、') : '无'}
              </Descriptions.Item>
              <Descriptions.Item label="可审核金额上限">
                {auth?.amountLimitCents === null || auth?.amountLimitCents === undefined
                  ? '不限'
                  : `¥${(auth.amountLimitCents / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`}
              </Descriptions.Item>
              <Descriptions.Item label="可放行的最高风险等级">
                {auth?.maxRiskLevel ? RISK_LEVEL_LABELS[auth.maxRiskLevel] : '不限'}
              </Descriptions.Item>
            </Descriptions>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              数据权限在服务端自动改写查询范围（本人 / 本部门及以下 / 本租户 / 自定义），
              前端仅做展示；金额上限与风险等级用于财务初审的放行判断。
            </Typography.Paragraph>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={`我的功能权限（${myPermissions.length} 项）`}
            size="small"
            extra={
              <Button
                size="small"
                disabled={!isTenantUser}
                title={isTenantUser ? undefined : '平台账号暂不支持自助改密码，请联系系统管理员'}
                onClick={() => {
                  pwdForm.resetFields();
                  setPwdOpen(true);
                }}
              >
                修改密码
              </Button>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {Object.entries(byModule).map(([module, items]) => (
                <div key={module}>
                  <Typography.Text strong>{module}</Typography.Text>
                  <div style={{ marginTop: 4 }}>
                    <Space wrap size={[4, 4]}>
                      {items.map((p) => (
                        <Tag key={p.code} color="blue">
                          {p.name}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                </div>
              ))}
              {myPermissions.length === 0 ? (
                <Typography.Text type="secondary">
                  当前账号未分配功能权限（平台运营人员不使用租户权限码，属正常情况）。如有疑问请联系租户管理员。
                </Typography.Text>
              ) : null}
            </Space>
          </Card>

          <Card title={`未授予的权限（${missingModules.length} 项）`} size="small" style={{ marginTop: 16 }}>
            <Table
              size="small"
              rowKey="code"
              pagination={false}
              scroll={{ y: 260, x: 'max-content' }}
              dataSource={missingModules}
              columns={[
                { title: '权限', dataIndex: 'name', width: 130 },
                { title: '模块', dataIndex: 'module', width: 100 },
                { title: '权限码', dataIndex: 'code', width: 190, render: (v: string) => <span className="ilc-mono" style={{ fontSize: 12 }}>{v}</span> },
                { title: '说明', dataIndex: 'description', ellipsis: true },
              ]}
              locale={{ emptyText: '已拥有全部权限' }}
            />
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              共 {PERMISSION_CATALOG.length} 项权限；权限清单来自服务端目录（{Object.keys(PERMISSION_BY_CODE).length} 项），
              前端与后端使用同一份定义。
            </Typography.Paragraph>
          </Card>
        </Col>
      </Row>

      {/* 修改自己的密码。
          必须校验当前密码：仅有登录态不足以证明操作者是本人，
          否则令牌被盗后攻击者可以直接改密码，把账号主人锁在外面。 */}
      <Modal
        open={pwdOpen}
        title="修改密码"
        onCancel={() => setPwdOpen(false)}
        onOk={() => void changePassword()}
        confirmLoading={changing}
        okText="确认修改"
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="修改后立即生效"
          description="当前登录不会中断，但下次登录需要用新密码。如果这个密码是平台或管理员下发的，建议尽快改掉。"
        />
        <Form form={pwdForm} layout="vertical" preserve={false}>
          <Form.Item
            name="currentPassword"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            extra="至少 8 位，需同时包含字母与数字"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '密码至少 8 位' },
              { pattern: /[a-zA-Z]/, message: '密码需包含字母' },
              { pattern: /\d/, message: '密码需包含数字' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator: (_r, value) =>
                  !value || getFieldValue('newPassword') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error('两次输入的密码不一致')),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
